import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { createTheme } from '@mui/material/styles';
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import IResetPass from "../../types/ResetPass";
import IAlert from "../../types/Alert";
import ILog from "../../types/Log";
import LogService from "../../services/LogService";
import { Alert, Avatar, Button, CircularProgress, Container, Paper, Snackbar, Typography } from "@mui/material";
import LockIcon from '@mui/icons-material/Lock';
import * as constantes from '../../common/Constantes';
import AuthService from "../../services/AuthService";
import PasswordField from "../utilities/PasswordField";

type ResetPassForm = {
    password: string;
    passwordConfirm: string;
};

const schema = yup.object({
    password: yup.string().required('La contraseña es obligatoria').min(8, 'La contraseña debe tener al menos 8 caracteres').matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*\W).{8,}$/, 'Debe contener una mayúscula, una minúscula, un número y un carácter especial'),
    passwordConfirm: yup.string().required('La confirmación de contraseña es obligatoria').oneOf([yup.ref('password'), null], 'Las contraseñas deben coincidir')
});

const CreatePassEmpleado = () => {
    const theme = createTheme();
    const history = useNavigate();

    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState<IAlert>({ open: false, severity: 'info', msg: '' });
    const {register, handleSubmit, formState: { errors }} = useForm<ResetPassForm>({ resolver: yupResolver(schema) });

    const onSubmit: SubmitHandler<ResetPassForm> = (data: ResetPassForm) => {
        setLoading(true);
        createPass({...data, _id: '', token: ''});
    };

    const handleCloseAlert = (_?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
          return;
        }
        setAlert({ open: false, severity: 'info', msg: '' });
    };

    const createPass = (data: IResetPass) => {
        const urlParams = new URLSearchParams(window.location.search);
        const id = urlParams.get(constantes.QP_ID);
        const token = urlParams.get(constantes.QP_TOKEN);

        if (!id || !token) {
            setAlert({ open: true, severity: 'error', msg: 'El token no es válido o ha expirado' });
            setLoading(false);
            return;
        }

        data._id = id;
        data.token = token;

        AuthService.createPassEmpleado(data._id, data)
        .then((response: any) => {
            setLoading(false);
            setAlert({ open: true, severity: 'success', msg: response.data.message });
            setTimeout(() => {
                history(constantes.R_HOME);
            }, constantes.timeRedirect)
        })
        .catch ((e: any) => {
            const error: ILog = { method: 'CreatePassEmpleado.Component.createPassEmpleado', level: 'error', message: e.message, meta: e.response };
            LogService.logError(error);
            setAlert({ open: true, severity: 'error', msg: e.response.data.message });
            setLoading(false);
        });
    };

    try {
        return (
            <>
            <Container maxWidth="sm">
                <Paper elevation={3} sx={{ marginTop: theme.spacing(3), marginBottom: theme.spacing(3), padding: theme.spacing(3), display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Avatar sx={{ margin: theme.spacing(1), backgroundColor: theme.palette.secondary.main }}>
                        <LockIcon />
                    </Avatar>
    
                    <>
                    <Typography variant="h4" sx={{ textAlign: 'center', margin: theme.spacing(1, 0, 1) }}>Crear contraseña</Typography>
                    <form onSubmit={handleSubmit(onSubmit)} noValidate>
                        <PasswordField<ResetPassForm>
                          register={register}
                          errors={errors}
                          name="password"
                          label="Contraseña"
                        />

                        <PasswordField<ResetPassForm>
                          register={register}
                          errors={errors}
                          name="passwordConfirm"
                          label="Confirmar contraseña"
                        />

                        <Button
                          sx={{ margin: theme.spacing(3, 0, 2) }}
                          variant="contained"
                          color="primary"
                          fullWidth
                          type="submit"
                        >
                          {loading ? <CircularProgress size={25} color="inherit" /> : `Enviar`}
                        </Button>
                    </form>
                    </>

                </Paper>
            </Container>

            <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
                <Alert variant="filled" onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>{alert.msg}</Alert>
            </Snackbar>
            </> 
        );
    } catch (e: any) {
        const error: ILog = { method: 'CreatePassEmpleado.Component', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
        return (
            <Alert severity="error">{e.message}</Alert>
        );
    }
};

export default CreatePassEmpleado;